import React, { useCallback, useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Editorial bottle carousel: auto-rotates, pauses on interaction, swipe on mobile. */
const ShowcaseCarousel = ({ items, onSelect }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef(null);

  const scrollToIndex = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const el = track.children[i];
    if (!el) return;
    const left = el.offsetLeft - (track.clientWidth - el.clientWidth) / 2;
    track.scrollTo({ left, behavior: 'smooth' });
  }, []);

  // Determine active card from scroll position
  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let best = 0; let bestDist = Infinity;
    Array.from(track.children).forEach((el, i) => {
      const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  }, []);

  const pauseFor = useCallback((ms = 6000) => {
    setPaused(true);
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), ms);
  }, []);

  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const t = setInterval(() => {
      const next = (active + 1) % items.length;
      scrollToIndex(next);
    }, 4200);
    return () => clearInterval(t);
  }, [active, paused, items.length, scrollToIndex]);

  useEffect(() => { setActive(0); trackRef.current?.scrollTo({ left: 0 }); }, [items]);

  const step = (dir) => { pauseFor(); scrollToIndex(Math.max(0, Math.min(items.length - 1, active + dir))); };

  return (
    <div
      className="corp-carousel"
      data-testid="showcase-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => pauseFor(8000)}
    >
      <button className="corp-carousel-arrow corp-carousel-prev" aria-label="Previous" onClick={() => step(-1)}
              disabled={active === 0} data-testid="showcase-prev">‹</button>
      <div className="corp-carousel-track" ref={trackRef} onScroll={onScroll}>
        {items.map((it, i) => (
          <article
            key={it.id}
            className={`corp-bottle-card ${i === active ? 'is-active' : ''} ${it.featured ? 'is-featured' : ''}`}
            data-testid={`showcase-item-${it.id}`}
            role="button"
            tabIndex={0}
            onClick={() => { pauseFor(); i === active ? onSelect(it) : scrollToIndex(i); }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                pauseFor();
                i === active ? onSelect(it) : scrollToIndex(i);
              }
            }}
          >
            <div className="corp-bottle-img-wrap">
              <img src={`${BACKEND_URL}${it.bottleUrl}`} alt={`${it.name} custom wine label on a Lokal bottle`} loading="lazy" draggable={false} />
            </div>
            <div className="corp-bottle-caption">
              <p className="corp-bottle-cat">{it.subcategoryLabel}</p>
              <h3 className="corp-bottle-name">{it.name}</h3>
              {it.useCase && <p className="corp-bottle-use">{it.useCase}</p>}
            </div>
          </article>
        ))}
      </div>
      <button className="corp-carousel-arrow corp-carousel-next" aria-label="Next" onClick={() => step(1)}
              disabled={active === items.length - 1} data-testid="showcase-next">›</button>
      <div className="corp-carousel-dots" aria-hidden="true">
        {items.map((it, i) => (
          <span key={it.id} className={i === active ? 'on' : ''} data-testid={`showcase-dot-${it.id}`} onClick={() => { pauseFor(); scrollToIndex(i); }} />
        ))}
      </div>
    </div>
  );
};

export default ShowcaseCarousel;
